import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Descriptions,
  Spin,
  Typography,
  Collapse,
  Skeleton,
  Divider,
  Empty
} from 'antd';
import ContainerBody from '@components/ContainerBody';
import Breadcrumb from '@components/Breadcrumb';
import { useParams } from 'react-router-dom';
import { apiGetDeviceById } from '@services/ManageDevices/DeviceServices';
import './styles.css';
import moment from 'moment';
import { convertPN, formatCurrency } from '@utils/utils';

const ViewItem = ({ label, content, children }) => (
  <Col md={8} sm={12}>
    <div className="view_item item__view">
      <div className="view_item__label">{label}</div>
      <div className="view_item__content">{content ?? 'N/A'}</div>
      {children}
    </div>
  </Col>
);

const { Panel } = Collapse;

const DeviceInformationDetail = () => {
  const { id } = useParams(); // Get serial_no or customer_id from the URL params
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const [error, setError] = useState(null); // Error state for any potential API issues

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiGetDeviceById(id);
        setDeviceData(response.data?.data);
      } catch (error) {
        setError('Failed to fetch data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (error) {
    return <div>{error}</div>;
  }
  const _3_0 = deviceData ? JSON.parse(deviceData?.['/3/0']) : {};
  const _80_0 = deviceData ? JSON.parse(deviceData?.['/80/0']) : {};
  const _82_0 = deviceData ? JSON.parse(deviceData?.['/82/0']) : {};
  const _84_0 = deviceData ? JSON.parse(deviceData?.['/84/0']) : {};
  const _99_0 = deviceData ? JSON.parse(deviceData?.['/99/0']) : {};
  const installTime = new Date(deviceData?.created_at).getTime();
  const lastSeenAt = deviceData?.last_seen_at;
  const timeDifference = lastSeenAt - installTime;
  let remainingBatCap =
    ((8500 * 1000 -
      (timeDifference * 30) / (3600 * 1000) -
      deviceData?.number_of_updates * 1394.52) /
      (8500 * 1000)) *
    100;
  if (isNaN(remainingBatCap)) remainingBatCap = 0;
  remainingBatCap = Math.max(0, Math.min(100, remainingBatCap));

  const WarningDetails = ({ record }) => {
    const info = JSON.parse(record['/82/0']);
    const magneticAttackStatus = info?.magneticAttackStatus;
    const antiDemolitionStatus = info?.antiDemolition;
    const happenedMagneticAttackStatus = info?.happenedMagneticAttack;
    const happenedAntiDemolitionStatus = info?.happenedAntiDemolition;
    const overFlowStatus = info?.overFlowStatus;
    const stopStatus = info?.stopStatus;

    const reverseFlows = record?.denseData_diffirenceOfTheFlowPulseNumber?.filter(
      (item) => item < 0
    )?.length;

    return (
      <Row gutter={[8, 8]}>
        <ViewItem
          label="Đã bị tấn công từ tính"
          content={happenedMagneticAttackStatus ? 'Có' : 'Không'}
        />
        <ViewItem
          label="Đang bị tấn công từ tính"
          content={magneticAttackStatus ? 'Có' : 'Không'}
        />
        <ViewItem
          label="Đã bị tấn công tháo rời"
          content={happenedAntiDemolitionStatus ? 'Có' : 'Không'}
        />
        <ViewItem
          label="Đang bị tấn công tháo rời"
          content={antiDemolitionStatus ? 'Có' : 'Không'}
        />
        <ViewItem label="Dòng chảy vượt ngưỡng" content={overFlowStatus ? 'Có' : 'Không'} />
        <ViewItem label="Cảnh báo kẹt công tơ" content={stopStatus ? 'Có' : 'Không'} />
        <ViewItem label="Dòng chảy ngược" content={reverseFlows ? 'Có' : 'Không'} />
      </Row>
    );
  };

  const InteractionDetails = ({ record }) => {
    const info = JSON.parse(record['/99/0']);

    return (
      <Row gutter={[8, 8]}>
        <ViewItem label="Cường độ sóng (dBm)" content={(info?.rsrp * 0.1)?.toFixed(2)} />
        <ViewItem label="Tỷ lệ tín/tạp (dB)" content={(info?.snr * 0.1)?.toFixed(2)} />
        <ViewItem label="Địa chỉ IP/Port Server" content={info?.ip} />
        <ViewItem label="Số IMSI" content={record?.imsi} />
        <ViewItem label="Số ICCID SIM" content={record?.iccid} />
      </Row>
    );
  };
  const ReportingConfigDetails = ({ record }) => {
    const config = JSON.parse(record['/84/0']);

    return (
      <Row gutter={[8, 8]}>
        <ViewItem label="Chu kỳ gửi báo cáo (phút)" content={config?.reportPeriod / 60} />
        <ViewItem label="Thời điểm gửi báo cáo" content={config?.reportTimeSlot} />
        <ViewItem label="Số lần truyền lại" content={config?.retries} />
        <ViewItem label="Chu kỳ truyền lại (giây)" content={config?.retryPeriod} />
        <ViewItem label="Khoảng thời gian gửi báo cáo" content={config?.reportingInterval} />
      </Row>
    );
  };
  const CustomerDetails = ({ record }) => {
    const customerInfo = record?.customer; // Adjust the path as needed

    return (
      <Row gutter={[8, 8]}>
        <ViewItem label="CN" content={customerInfo?.branch} />
        <ViewItem label="TQL" content={customerInfo?.manage_group} />
        <ViewItem label="ĐHK" content={customerInfo?.parent_device} />
        <ViewItem label="Tuyến" content={customerInfo?.route} />
        <ViewItem label="DBĐD" content={customerInfo?.code} />
        <ViewItem label="Tên KH" content={customerInfo?.name} />
        <ViewItem label="Địa chỉ" content={customerInfo?.address} />
        <ViewItem label="Serie ĐH" content={customerInfo?.serial_number} />
        <ViewItem label="Chỉ số (lít)" content={record?.meter_reading} />
        <ViewItem label="Số ĐT" content={customerInfo?.phone} />
        <ViewItem label="Tên ĐH" content={customerInfo?.device_name} />
        <ViewItem
          label="Ngày lắp"
          content={moment(customerInfo?.install_time).format('DD-MM-YYYY')}
        />
        <ViewItem label="Cỡ ĐH" content={customerInfo?.size} />
        <ViewItem label="Loại ĐH" content={customerInfo?.type} />
        <ViewItem
          label="Ngày kiểm định"
          content={moment(customerInfo?.inspection_date).format('DD-MM-YYYY')}
        />
        <ViewItem label="Số năm hiệu lực" content={customerInfo?.year_valid} />
        <ViewItem label="Tọa độ lắp đặt" content={'Không xác định'} />
      </Row>
    );
  };

  const MainDeviceInfo = ({ record }) => {
    const info30 = JSON.parse(record['/3/0']);
    const info80 = JSON.parse(record['/80/0']);

    return (
      <>
        <Row gutter={[8, 8]}>
          <ViewItem label="Model" content={deviceData.model} />
          <ViewItem label="Số Sêri" content={deviceData.serial_no} />
          <ViewItem label="Số IMEI" content={deviceData?.imei} />
          <ViewItem label="Điện áp pin(V)" content={(_3_0?.powerSourceVoltage / 100)?.toFixed(2)} />
          <ViewItem
            label="Thời điểm gửi báo cáo"
            content={moment.unix(_3_0?.currentTime).format('DD-MM-YYYY HH:mm:ss')}
          />
          <ViewItem
            label="Thời điểm đăng ký"
            content={moment(deviceData?.created_at).format('DD-MM-YYYY HH:mm:ss')}
          />
          <ViewItem
            label="Thời điểm lắp đặt"
            content={
              deviceData?.install_time
                ? moment(deviceData?.install_time).format('DD-MM-YYYY HH:mm:ss')
                : moment(deviceData?.created_at).format('DD-MM-YYYY HH:mm:ss')
            }
          />
          <ViewItem label="Số lần cập nhật dữ liệu" content={deviceData.number_of_updates} />
          <ViewItem label="Phần trăm pin còn lại(%)" content={remainingBatCap?.toFixed(0)} />
          <ViewItem label="Phiên bản phần cứng" content={info30?.hardwareVersion} />
          <ViewItem label="Chế độ đo lường" content={info80?.measurementModel} />
          <ViewItem
            label="Hệ số xung(Lít/Xung)"
            content={convertPN?.find((item) => item?.value === info80?.pn)?.label}
          />
          <ViewItem label="Chỉ số tối đa(Lít)" content={info80?.maxReading} />
        </Row>
      </>
    );
  };

  const MeasurementDetails = ({ record }) => {
    const info = JSON.parse(record['/80/0']);
    return (
      <>
        <Row gutter={[8, 8]}>
          <ViewItem
            label="Thời điểm nhận báo cáo"
            content={new Intl.DateTimeFormat('vi-VN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            }).format(deviceData?.last_seen_at)}
          />
          <ViewItem
            label="Lượng nước chảy xuôi trong ngày (lít) "
            content={Math.round(record?.water_positive_of_the_day)}
          />
          <ViewItem
            label="Lượng nước chảy ngược trong ngày (lít)"
            content={Math.round(record?.water_reverse_of_the_day)}
          />
          <ViewItem
            label="Lượng nước tiêu thụ trong ngày (lít) "
            content={Math.round(record?.water_consumption_of_the_day)}
          />
          <ViewItem label="Chỉ số công tơ(lít)" content={info?.meterReading} />
          <ViewItem label="Thời điểm bắt đầu ghi dữ liệu dày đặc" content={null} />
          <ViewItem
            label="Chỉ số công tơ tại thời điểm bắt đầu ghi dữ liệu dày đặc"
            content={null}
          />
          <ViewItem label="Lượng nước tiêu thụ sau mỗi chu kỳ ghi dữ liệu dày đặc" content={null} />
          <ViewItem label="Chu kỳ ghi dữ liệu dày đặc" content={null} />
          <ViewItem label="Lượng Tiêu Thụ từ đầu tháng" content={null} />
          <ViewItem label="Lượng tiêu thụ tháng gần nhất" content={null} />
          <ViewItem label="Lượng tiêu thụ tháng trước tháng gần nhất" content={null} />
          <ViewItem label="Lượng tiêu thụ tháng thứ 3 trước tháng này" content={null} />
        </Row>
      </>
    );
  };
  const ReplacementInfo = ({ record }) => {
    const old_devices = record?.old_devices;
    return (
      <>
        {old_devices?.length ? (
          old_devices?.map((itemOldDevice, index) => {
            return (
              <>
                <Typography.Text style={{ fontWeight: '500', marginBottom: '16px' }}>
                  {' '}
                  Thiết bị {index + 1}:{' '}
                </Typography.Text>
                <Row gutter={[8, 8]}>
                  <ViewItem label="Model" content={itemOldDevice.model} />
                  <ViewItem label="Số Sêri" content={itemOldDevice.serial_no} />
                  <ViewItem label="Chỉ số cuối cùng" content={itemOldDevice?.meter_reading} />
                  <ViewItem label="Ngày thay thế" content={itemOldDevice?.replace_date} />
                  <ViewItem label="Lý do thay thế" content={itemOldDevice?.remark} />
                </Row>
                <Divider />
              </>
            );
          })
        ) : (
          <>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có dữ liệu thay thế" />
          </>
        )}
      </>
    );
  };
  return (
    <ContainerBody>
      <div className="breadcrumb">
        <Breadcrumb
          homePath={'/dashboard'}
          routeSegments={[{ name: 'Chi tiết thông số thiết bị' }]}
        />
      </div>
      {loading ? (
        <Skeleton active />
      ) : (
        <Collapse defaultActiveKey={['1', '2', '3', '4', '5', '6']} expandIconPosition="end">
          <Panel header="Thông tin về thiết bị ghi số" key="1">
            <MainDeviceInfo record={deviceData} />
          </Panel>
          <Panel header="Thông tin đo lường" key="2">
            <MeasurementDetails record={deviceData} />
          </Panel>
          <Panel header="Thông tin cảnh báo" key="3">
            <WarningDetails record={deviceData} />
          </Panel>
          <Panel header="Thông tin tương tác NB" key="4">
            <InteractionDetails record={deviceData} />
          </Panel>
          <Panel header="Thông số cấu hình báo cáo" key="5">
            <ReportingConfigDetails record={deviceData} />
          </Panel>
          <Panel header="Thông tin khách hàng" key="6">
            <CustomerDetails record={deviceData} />
          </Panel>
          <Panel header="Thông tin thay thế thiết bị" key="6">
            <ReplacementInfo record={deviceData} />
          </Panel>
        </Collapse>
      )}
    </ContainerBody>
  );
};

export default DeviceInformationDetail;
